import { Button } from 'react-bootstrap'
import { Edit } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import capitalise from '../../../utils/capitalise'
import formatClubName from '../../../utils/formatClubName'
import ButtonWithTooltip from '../../../components/buttons/ButtonWithTooltip'
import useAuth from '../../../hooks/useAuth'
import getPosition from '../../../utils/getPosition'
import isMembershipPayable from '../../../utils/membership/isMembershipPayable'
import isPaymentDue from '../../../utils/membership/isPaymentDue'

const ViewUserCard = ({ data, setEditMode }) => {
  const { role } = useAuth()
  const navigate = useNavigate()
  const { firstName, lastName, email, dob, address, phoneNumber, accountType, clubs, membership, uid } = data

  const handleEditClicked = () => setEditMode(state => !state)

  const handleClickToRenew = () => navigate('/payments')

  const latestPaymentDate = membership?.latestPaymentDate
  const validUntil = membership?.validUntil
  const membershipPayable = isMembershipPayable(validUntil)
  const paymentDue = isPaymentDue(validUntil)
  const formattedDate = new Date(dob).toLocaleDateString('en-GB') || null
  const lastPaymentMade = latestPaymentDate
    ? new Date(membership?.latestPaymentDate).toLocaleDateString('en-GB')
    : 'N/A'
  const formattedValidUntil = validUntil ? new Date(validUntil).toLocaleDateString('en-GB') : 'N/A'

  let membershipMessage = null

  if (membershipPayable) {
    if (paymentDue) {
      membershipMessage = (
        <div className="d-flex flex-column align-items-center justify-content-center gap-4 my-2 text-center">
          <h2 className="text-danger text-center fw-bold m-0">Membership payment is now due</h2>
          <h5 className="fw-bold m-0">Please make payment before 1st November to keep your membership benefits</h5>
          <Button onClick={handleClickToRenew} variant="info" className="rounded-3 p-3 border-0">
            <span className="fs-5 fw-bold">Click to renew</span>
          </Button>
        </div>
      )
    } else {
      const thisYear = new Date().getFullYear() % 100
      const nextYear = thisYear + 1
      membershipMessage = (
        <div className="d-flex flex-column align-items-center justify-content-center gap-4 my-2 text-center">
          <h2 className="text-info text-center fw-bold m-0">
            Membership renewal {thisYear}/{nextYear} now open
          </h2>
          <Button onClick={handleClickToRenew} variant="info" className="rounded-3 p-3 border-0">
            <span className="fs-5 fw-bold">Click to renew</span>
          </Button>
        </div>
      )
    }
  }

  return (
    <div className="w-100 mx-auto shadow-sm rounded-4 p-4 bg-white text-start">
      {membershipMessage && membershipMessage}
      <div className="d-flex justify-content-between align-items-center">
        <h4 className="my-3 fw-bold ps-2">Basic Details</h4>
        {role === 'admin' && (
          <ButtonWithTooltip text="Edit" className="border-0" action={setEditMode} delay={750}>
            <Button
              onClick={handleEditClicked}
              className="me-2 no-arrow d-flex align-items-center justify-content-center rounded-circle p-2 bg-white shadow-btn border"
              variant=""
            >
              <Edit />
            </Button>
          </ButtonWithTooltip>
        )}
      </div>

      <div>
        <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
          <b className="text-muted w-100 mb-1">Name</b>
          <div className="w-100 fw-bold">
            {firstName} {lastName}
          </div>
        </div>
        <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
          <b className="text-muted w-100 mb-1">Date of Birth</b>
          <div className="w-100 fw-bold">{formattedDate}</div>
        </div>
        <div className="d-flex flex-column flex-sm-row  border-bottom p-2 custom-hover-tr">
          <b className="text-muted w-100 my-1">Address</b>
          <div className="w-100 fw-bold">
            <p className="m-0">{address?.streetAddress}</p>
            <p className="m-0">{address?.city}</p>
            <p className="m-0">{address?.county}</p>
            <p className="m-0">{address?.postcode}</p>
            <p className="m-0">{address?.country}</p>
          </div>
        </div>
        <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
          <b className="text-muted w-100 mb-1">Phone Number</b>
          <div className="w-100 fw-bold">{phoneNumber}</div>
        </div>
        <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
          <b className="text-muted w-100 mb-1">Email</b>
          <div
            className="w-100 fw-bold"
            style={{
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
            }}
          >
            {email}
          </div>
        </div>
        <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
          <b className="text-muted w-100 mb-1">GBM Number</b>
          <div className="w-100 fw-bold">{uid}</div>
        </div>
        <div className="border-white">
          <div colSpan={2} className="p-0 ps-2">
            <p className="mb-3 mt-5 fw-bold h4">Membership</p>
          </div>
        </div>
        <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
          <b className="text-muted w-100 mb-1">Membership Type</b>
          <div className="w-100 fw-bold">{capitalise(accountType)}</div>
        </div>
        <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
          <b className="text-muted w-100 mb-1">Membership Status</b>
          {paymentDue ? (
            <div className="w-100 fw-bold text-danger">Payment Due</div>
          ) : (
            <div className="w-100 fw-bold text-success">Paid</div>
          )}
        </div>
        <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
          <b className="text-muted w-100 mb-1">Latest Payment</b>
          <div className="w-100 fw-bold">{lastPaymentMade}</div>
        </div>
        <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
          <b className="text-muted w-100 mb-1">Membership Valid Until</b>
          <div className="w-100 fw-bold">{formattedValidUntil}</div>
        </div>
        <>
          {clubs.length > 0 && (
            <>
              <div className="border-white">
                <div colSpan={2} className="p-0 ps-2">
                  <p className="mb-0 mt-5 fw-bold h4">Clubs</p>
                </div>
              </div>
              {clubs.map(club => {
                return <ClubCard key={club.clubId} club={club} />
              })}
            </>
          )}
        </>
      </div>
    </div>
  )
}

export default ViewUserCard

const ClubCard = ({ club }) => {
  const { clubName = '', clubId = '', positions = [] } = club

  return (
    <>
      <div className="border-white">
        <div colSpan={2} className="p-0 ps-2">
          <p className="mb-2 mt-4 fw-bold h5">{formatClubName(clubName)}</p>
        </div>
      </div>
      <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
        <b className="text-muted w-100 mb-1">Club Number</b>
        <div className="w-100 fw-bold">{clubId}</div>
      </div>
      <div className="d-flex flex-column flex-sm-row align-items-start border-bottom p-2 custom-hover-tr">
        <b className="text-muted w-100 mb-1 text-top">Positions</b>
        <div className="w-100 fw-bold">
          {positions.length > 0 ? (
            positions.map(position => (
              <p key={position} className="m-0">
                {getPosition(position)}
              </p>
            ))
          ) : (
            <p className="m-0">Member</p>
          )}
        </div>
      </div>
    </>
  )
}
